import { createApiRef, DiscoveryApi, IdentityApi, ConfigApi } from '@backstage/core-plugin-api';

export const mlflowApiRef = createApiRef<MLFlowApi>({
  id: 'plugin.mlflow.service',
});

export interface MLFlowTag {
  key: string;
  value: string;
}

export interface MLFlowModelVersion {
  name: string;
  version: string;
  creation_timestamp: number;
  last_updated_timestamp: number;
  user_id?: string;
  current_stage: string;
  description?: string;
  source?: string;
  run_id?: string;
  status: string;
  status_message?: string;
  tags?: MLFlowTag[];
  run_link?: string;
  aliases?: string[];
  model_id?: string;
  deployment_job_state?: string;
}

export interface MLFlowAlias {
  alias: string;
  version: string;
}

export interface MLFlowRegisteredModel {
  name: string;
  creation_timestamp?: number;
  last_updated_timestamp?: number;
  user_id?: string;
  description?: string;
  latest_versions?: MLFlowModelVersion[];
  tags?: MLFlowTag[];
  aliases?: MLFlowAlias[];
}

export interface MLFlowRegisteredModelDetail {
  [x: string]: any;
  registered_model: MLFlowRegisteredModel;
}

export interface MLFlowSearchResponse {
  registered_models: MLFlowRegisteredModel[];
  next_page_token?: string;
}

export interface WorkflowTriggerInput {
  components: string[];
  device_name: string;
  model_version?: string;
}

export interface TrainInput {
  DATASET_NAME: string;
  TASK_TYPE: string;
  project_name: string;
  model_description?: string;
  model_name: string;
  createdBy: string;
  models: Array<{
    MODEL_VERSION: string;
    MODEL_SIZE?: string; // Optional
  }>;
  // All augmentation parameters nested under rest_env_vars
  rest_env_vars: Record<string, string | number>;
}

/**
 * Represents a single job within a batch training response
 * Note: The `belongs_to` field is the batchId, not the model name
 */
export interface BatchJob {
  job_id: string;
  ecs_task_arn: string | null;
  status: string;
  belongs_to: string; // This is the parentbatchId
  model_version: string;
  model_size: string;
}

export interface BatchTrainingResponse {
  batch_id: string;
  project_name: string;
  createdBy: string;
  dataset_name: string;
  model_description: string;
  model_name: string;
  startedAt: string;
  lastUpdated: string;
  jobs: BatchJob[];
}

export interface BatchStatusResponse {
  batches: Array<{
    batch_id: string;
    status: string; // 'INITIATED' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED'
    lastUpdated: string;
    jobs: Array<{
      job_id: string;
      status: string;
    }>;
  }>;
}

export interface RegisterModelsResponse {
  results: Array<{
    job_id: string;
    registered: boolean;
    backstage_status: string;
    message: string;
  }>;
}

// Legacy TrainResponse kept for backward compatibility
export interface TrainResponse {
  jobs: Array<{
    job_id: string;
    ecs_task_arn: string | null;
    status: string;
    input_s3_bucket: string;
    input_s3_prefix: string;
    output_s3_bucket: string;
    output_s3_prefix: string;
    output_s3_uri: string;
    project_name: string;
    model_name: string;
    backstage_status: string;
    backstage_error: string | null;
  }>;
}

export interface TrainingStatusResponse {
  batch_id?: string; // Batch identifier
  batch_status?: string; // Batch-level status: 'INITIATED' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED'
  last_updated_at?: string; // Batch-level last updated timestamp
  jobs: Array<{
    job_id: string;
    status: string;
  }>;
}

export interface DeploymentLog {
  timestamp: string;
  message: string;
}

export interface WorkflowTriggerResponse {
  success: boolean;
  message?: string;
  run_id?: number;
  deployment_id?: string;
  status?: string;
  logs?: DeploymentLog[];
}

export interface DeploymentStatusResponse {
  deployment_id: string;
  status: string;
  message?: string;
  status_message?: string;
  created_at?: string;
  updated_at?: string;
  error?: string;
  logs?: DeploymentLog[];
}

export interface Device {
  id: string;
  name: string;
  status?: string;
  type?: string;
  location?: string;
  [key: string]: any; // Allow additional properties
}

export interface DevicesResponse {
  devices: Device[];
  total?: number;
}

export interface MLFlowApi {
  getRegisteredModel(name: string): Promise<MLFlowRegisteredModelDetail>;
  listRegisteredModels(maxResults?: number): Promise<MLFlowRegisteredModel[]>;
  searchRegisteredModels(
    filter?: string,
    maxResults?: number,
    orderBy?: string[],
    pageToken?: string,
  ): Promise<MLFlowSearchResponse>;
  getModelVersions(modelName: string): Promise<MLFlowModelVersion[]>;
  healthCheck(): Promise<{ status: string; message?: string }>;
  triggerWorkflow(input: WorkflowTriggerInput): Promise<WorkflowTriggerResponse>;
  getDeploymentStatus(deploymentId: string): Promise<DeploymentStatusResponse>;
  getDevices(): Promise<Device[]>;
  trainModel(input: TrainInput): Promise<BatchTrainingResponse>;
  getTrainingStatus(batchId: string, signal?: AbortSignal): Promise<TrainingStatusResponse>;
  getTrainingStatusBatch(batchIds: string[], signal?: AbortSignal): Promise<BatchStatusResponse>;
  getAllBatches(): Promise<BatchTrainingResponse[]>;
  getBatchDetails(batchId: string): Promise<BatchTrainingResponse>;
  registerModels(jobIds: string[]): Promise<RegisterModelsResponse>;
}

export class MLFlowClient implements MLFlowApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;
  private readonly trainingApiBaseUrl: string;
  private readonly trainingApiPrefix: string;

  constructor(options: { discoveryApi: DiscoveryApi; identityApi: IdentityApi; configApi: ConfigApi }) {
    if (!options.configApi) {
      throw new Error('MLFlowClient: configApi is required but was not provided');
    }
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;

    // Read required config values
    // These are marked with @visibility frontend in config.d.ts
    try {
      this.trainingApiBaseUrl = options.configApi.getString('mlflow.trainingApiBaseUrl');
    } catch (err: any) {
      throw new Error(
        `MLFlowClient: Missing required config 'mlflow.trainingApiBaseUrl'. ` +
        `Please ensure app-config.yaml or app-config.local.yaml contains:\n` +
        `mlflow:\n` +
        `  trainingApiBaseUrl: "http://your-api-url"`
      );
    }

    try {
      this.trainingApiPrefix = options.configApi.getString('mlflow.trainingApiPrefix');
    } catch (err: any) {
      throw new Error(
        `MLFlowClient: Missing required config 'mlflow.trainingApiPrefix'. ` +
        `Please ensure app-config.yaml or app-config.local.yaml contains:\n` +
        `mlflow:\n` +
        `  trainingApiPrefix: "/api/training"`
      );
    }
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('mlflow');
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const { token } = await this.identityApi.getCredentials();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async getRegisteredModel(name: string): Promise<MLFlowRegisteredModelDetail> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/models/${encodeURIComponent(name)}`;
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch MLFlow model '${name}': ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  async listRegisteredModels(maxResults?: number): Promise<MLFlowRegisteredModel[]> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    
    if (maxResults) {
      params.append('max_results', maxResults.toString());
    }

    const url = `${baseUrl}/models?${params.toString()}`;
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to list MLFlow models: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const data = await response.json();
    return data.models || [];
  }

  async searchRegisteredModels(
    filter?: string,
    maxResults?: number,
    orderBy?: string[],
    pageToken?: string,
  ): Promise<MLFlowSearchResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();
    
    if (filter) {
      params.append('filter', filter);
    }
    if (maxResults) {
      params.append('max_results', maxResults.toString());
    }
    if (orderBy && orderBy.length > 0) {
      for (const order of orderBy) {
        params.append('order_by', order);
      }
    }
    if (pageToken) {
      params.append('page_token', pageToken);
    }

    const url = `${baseUrl}/models/search?${params.toString()}`;
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to search MLFlow models: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  async getModelVersions(modelName: string): Promise<MLFlowModelVersion[]> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/models/${encodeURIComponent(modelName)}/versions`;
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch model versions for '${modelName}': ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const data = await response.json();
    return data.versions || [];
  }

  async healthCheck(): Promise<{ status: string; message?: string }> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/health`;
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return {
        status: 'error',
        message: 'Backend MLFlow service is not reachable',
      };
    }

    return response.json();
  }

  async triggerWorkflow(input: WorkflowTriggerInput): Promise<WorkflowTriggerResponse> {
    const url = `${this.trainingApiBaseUrl}/api/deploy`;
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to trigger workflow: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  async getDeploymentStatus(deploymentId: string): Promise<DeploymentStatusResponse> {
    const url = `${this.trainingApiBaseUrl}/api/deployments/${deploymentId}/status`;
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to get deployment status: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  async getDevices(): Promise<Device[]> {
    // Call external devices API directly
    const url = `${this.trainingApiBaseUrl}/api/devices`;
    const headers = await this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch devices: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const data = await response.json();
    
    // Handle different response formats
    if (Array.isArray(data)) {
      return data;
    } else if (data.devices && Array.isArray(data.devices)) {
      return data.devices;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    return [];
  }

  async trainModel(input: TrainInput): Promise<BatchTrainingResponse> {
    const url = `${this.trainingApiBaseUrl}${this.trainingApiPrefix}/batch-jobs`;
    const headers = await this.getAuthHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(input),
    });

    // Backend returns 202 Accepted for training initiation
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to start training: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  async getTrainingStatus(batchId: string, signal?: AbortSignal): Promise<TrainingStatusResponse> {
    const url = `${this.trainingApiBaseUrl}${this.trainingApiPrefix}/batch-job/status`;
    const headers = await this.getAuthHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ batches: [batchId] }),
      signal, // Support request cancellation
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to get training status: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const batchStatusResponse: BatchStatusResponse = await response.json();
    
    // Map BatchStatusResponse to TrainingStatusResponse format for compatibility
    const batchStatus = batchStatusResponse.batches[0];
    if (!batchStatus) {
      throw new Error('No batch status found in response');
    }

    return {
      batch_id: batchStatus.batch_id,
      batch_status: batchStatus.status, // Batch-level status: 'INITIATED' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED'
      last_updated_at: batchStatus.lastUpdated,
      jobs: batchStatus.jobs.map(job => ({
        job_id: job.job_id,
        status: job.status,
      })),
    };
  }

  /**
   * Get the statuses of multiple batches of training jobs
   * @param batchIds - The IDs of the batches to get the statuses of
   * @param signal - The signal to abort the request
   * @returns The statuses of the multiple batches of training jobs
   */
  async getTrainingStatusBatch(batchIds: string[], signal?: AbortSignal): Promise<BatchStatusResponse> {
    const url = `${this.trainingApiBaseUrl}${this.trainingApiPrefix}/batch-job/status`;
    const headers = await this.getAuthHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ batches: batchIds }),
      signal, // Support request cancellation
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to get training status batch: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const batchStatusResponse: BatchStatusResponse = await response.json();
    return batchStatusResponse;
  }

  async getAllBatches(): Promise<BatchTrainingResponse[]> {
    // GET all batches from new endpoint
    const url = `${this.trainingApiBaseUrl}${this.trainingApiPrefix}/batch-jobs`;
    const headers = await this.getAuthHeaders();

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to get all batches: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  async getBatchDetails(batchId: string): Promise<BatchTrainingResponse> {
    // GET specific batch details from new endpoint
    const url = `${this.trainingApiBaseUrl}${this.trainingApiPrefix}/batch-jobs/${batchId}`;
    const headers = await this.getAuthHeaders();

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 404) {
        const errorData = await response.json().catch(() => ({ detail: `Batch ${batchId} not found` }));
        throw new Error(errorData.detail || `Batch ${batchId} not found`);
      }
      const errorText = await response.text();
      throw new Error(
        `Failed to get batch details: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  async registerModels(jobIds: string[]): Promise<RegisterModelsResponse> {
    // POST to register models endpoint
    const url = `${this.trainingApiBaseUrl}${this.trainingApiPrefix}/register-models`;
    const headers = await this.getAuthHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ job_ids: jobIds }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to register models: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

}
